import useSignalFollowBotsList, {
  FollowSignalBotData,
} from "@shared/features/signals/useSignalFollowBotsList";
import { openMessage } from "utils/misc";
import {
  BotType,
  FollowSignalData,
  LanguageNameSpaces,
  OneClickFollowBotData,
} from "@shared/types";
import styled from "styled-components";
import ListItemActionTooltip from "features/signals/SignalFollow/ListItemActionTooltip";
import { ListItemWrapper } from "common/Lists/styles";
import useSearchInput from "utils/hooks/useSearchInput";
import IconWithFallback from "Global/IconWithFallback";
import { ICON_TYPES } from "@shared/common/constants";
import Text from "common/Typography/Text";
import { getBotAmountDisplay } from "@shared/features/bots/utils";
import { TABLET } from "common/constants";
import EmptyView from "common/Empty/EmptyView";
import React, { Fragment } from "react";
import { useHistory } from "react-router-dom";
import { appendSubGroupId, getCreateBotRoute } from "utils/routes";
import Button from "common/Buttons/Button";
import { EmptyStatesType } from "common/Icons/types.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { getSignalsBotsMigrationBannerTitle } from "@shared/features/configuration/utils.ts";
import { useBranding } from "@shared/utils/hooks/useBranding.ts";

interface SignalFollowBotsListProps {
  onListItemSelected: (botData?: OneClickFollowBotData) => void;
  signal?: FollowSignalData;
}

const SignalFollowBotsList = ({
  onListItemSelected,
  signal,
}: SignalFollowBotsListProps) => {
  const { isWhitelabel } = useBranding();
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const history = useHistory();
  const [SearchInput, searchValue] = useSearchInput({
    placeholder: tCommon("labels.search_placeholder"),
    style: { width: "100%", marginBottom: 12, marginTop: 4 },
    focusOnMount: false,
  });
  const { botsGroupsData, getBotAccountData } = useSignalFollowBotsList({
    openMessage,
    onListItemSelected,
    searchValue,
  });

  const getBotRow = (botData: FollowSignalBotData) => {
    const accountData = getBotAccountData(botData);
    return (
      <StyledItemWrapper
        key={botData.id}
        $invalid={!!botData.error}
        onClick={botData.onClick}
      >
        <IconWithFallback
          iconType={ICON_TYPES.EXCHANGE}
          coinOrExchangeName={accountData?.exchange}
          pxIconSize={24}
          iconStyle={{ marginRight: 8 }}
        />
        <div
          style={{
            flex: 1,
            overflow: "hidden",
            paddingRight: 20,
          }}
        >
          <div style={{ overflow: "hidden", display: "flex" }}>
            <Text $ellipsis={true}>{botData.name}</Text>
            <ListItemActionTooltip
              botData={botData}
              handleClick={botData.onClick}
            />
          </div>
          <Text type="secondary" $ellipsis={true}>
            {accountData?.name}
          </Text>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <Text type="secondary">{t("follow.labels.amount")}:</Text>
          <Text type="secondary">{getBotAmountDisplay(botData)}</Text>
        </div>
      </StyledItemWrapper>
    );
  };

  const actionButtonText = t("follow.actions.create_bot");
  const navigateCreateBot = () => {
    let route = getCreateBotRoute(BotType.SIGNAL);
    if (signal?.sub_group_id) {
      route = appendSubGroupId(route, signal?.sub_group_id);
    }
    history.push(route);
  };

  const getContent = () => {
    if (!botsGroupsData.length) {
      return (
        <EmptyView
          iconKey={EmptyStatesType.Bots}
          iconStyle={{ marginBottom: 12 }}
          description={getSignalsBotsMigrationBannerTitle(isWhitelabel)}
          buttonText={actionButtonText}
          buttonOnClick={navigateCreateBot}
          filtersChipsOpen={TABLET}
        />
      );
    }
    return (
      <>
        <div style={{ padding: "0 16px" }}>{SearchInput}</div>
        <ContentWrapper>
          {botsGroupsData.map((groupData, index) => {
            const content = groupData.isEmpty ? (
              <EmptyGroupWrapper>
                <EmptyGroupText type="secondary">
                  {groupData.emptyText}
                </EmptyGroupText>
                {groupData.showEmptyButton && (
                  <Button type="primary" onClick={navigateCreateBot}>
                    {actionButtonText}
                  </Button>
                )}
              </EmptyGroupWrapper>
            ) : (
              groupData.bots.map(getBotRow)
            );
            return (
              <Fragment key={index}>
                <SubTitle>{groupData.title}</SubTitle>
                {content}
              </Fragment>
            );
          })}
        </ContentWrapper>
      </>
    );
  };

  return <Wrapper>{getContent()}</Wrapper>;
};

export default SignalFollowBotsList;

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;
const ContentWrapper = styled.div`
  padding: 0 8px;
  flex: 1;
  overflow-y: auto;
`;
const StyledItemWrapper = styled(ListItemWrapper)<{ $invalid: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  overflow: hidden;
  padding: 8px 12px;
  border-radius: 6px;
  transition: background-color 0.2s;
  &:hover {
    background-color: ${(p) => (p.theme.isDarkMode ? "#34436D" : "#f9fafb")};
  }
  color: ${(p) => p.theme.colors.textColor};
  cursor: pointer;
  ${(props) =>
    props.$invalid &&
    `
    opacity: 0.4;
  `}
`;
const SubTitle = styled(Text)`
  color: ${(p) => p.theme.colors.secondaryGrey};
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  margin: 10px 0 10px 16px;
`;
const EmptyGroupWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
`;
const EmptyGroupText = styled(Text)`
  color: ${(p) => p.theme.colors.secondaryGrey};
  margin: 12px 0 10px 0;
  max-width: 300px;
  text-align: center;
`;
